import { Loading01Icon } from '@langchain/untitled-ui-icons';

import { forwardRef } from 'react';

import { cn } from '../../utils/cn';
import { Badge } from '../Badge';
import { Text } from '../Text';
import { buttonStyleMap } from './constants';

// Type to constrain decorators to only accept icons from @langchain/untitled-ui-icons
type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button size variant */
  size?: 'sm' | 'md';
  /** Button color scheme */
  color?: 'primary' | 'secondary' | 'error';
  /** Button visual variant */
  variant?: 'normal' | 'outlined' | 'plain' | 'underlined';
  /** Click handler */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Icon component from @langchain/untitled-ui-icons to display on the left side */
  leftDecorator?: IconComponent;
  /** Icon component from @langchain/untitled-ui-icons to display on the right side */
  rightDecorator?: IconComponent;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Tag text to display */
  tagText?: string;
  /** Position of the tag */
  tagPosition?: 'left' | 'right';
  /** Button content */
  children: React.ReactNode;
  /** Whether the button is loading */
  loading?: boolean;
}

const Spinner = () => {
  return <Loading01Icon className="size-4 animate-spin" />;
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      color = 'primary',
      variant = 'normal',
      size = 'sm',
      onClick,
      leftDecorator: LeftIcon,
      rightDecorator: RightIcon,
      disabled = false,
      tagText,
      tagPosition = 'right',
      className,
      children,
      loading = false,
      ...props
    },
    ref
  ) => {
    const baseStyles = cn(
      'inline-flex flex-none items-center justify-center gap-1.5 truncate transition-all duration-200',
      {
        'py-1': size === 'sm',
        'py-2': size === 'md',
      },
      {
        'px-2': variant !== 'underlined',
        'px-0 ': variant === 'underlined',
      },
      {
        'shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]':
          (variant === 'normal' || variant === 'outlined') && !disabled,
      },
      {
        // Rounded corners for normal and outlined variants
        [`rounded-${size}`]:
          variant === 'normal' || variant === 'outlined' || variant === 'plain',
        // No rounded corners for underlined
        'rounded-none': variant === 'underlined',
      }
    );

    // Generate unique class combinations for each color/variant combination
    const getColorVariantStyles = () => {
      // Generate the key for the current combination
      const state = disabled ? 'disabled' : 'enabled';
      const key = `${color}-${variant}-${state}` as keyof typeof buttonStyleMap;

      return buttonStyleMap[key] || '';
    };

    // Generate aria-label automatically based on content
    const getAriaLabel = () => {
      if (props['aria-label']) {
        return props['aria-label'];
      }

      // If children is a string, use it as aria-label
      if (typeof children === 'string') {
        return children;
      }
      // If children is not a string but tagText exists, use tagText
      if (tagText) {
        return tagText;
      }

      // Fallback to generic "button"
      return 'button';
    };

    const content = (
      <>
        {LeftIcon && (
          <LeftIcon
            className={cn('size-4 flex-shrink-0', '[&_path]:stroke-[1.5px]')}
          />
        )}

        {tagText && tagPosition === 'left' && (
          <Badge color={color} size="xs" rounded="xs">
            {tagText}
          </Badge>
        )}

        {typeof children === 'string' ? (
          <Text variant={size} weight="normal" as="span">
            {children}
          </Text>
        ) : (
          children
        )}

        {tagText && tagPosition === 'right' && (
          <Badge color={color} size="xs" rounded="xs">
            {tagText}
          </Badge>
        )}

        {RightIcon && (
          <RightIcon
            className={cn('size-4 flex-shrink-0', '[&_path]:stroke-[1.5px]')}
          />
        )}
      </>
    );

    return (
      <button
        ref={ref}
        type="button"
        className={cn(baseStyles, getColorVariantStyles(), className)}
        disabled={disabled}
        onClick={onClick}
        aria-label={getAriaLabel()}
        {...props}
      >
        {loading ? <Spinner /> : content}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };
export type { ButtonProps };
